/**
 * Phone Input Handler Service
 * Handles intl-tel-input initialization and synchronization with form fields
 * 
 * Architecture:
 * - JS/CSS: Bundled via webpack from npm package (self-contained)
 * - Flag images: Served from jsDelivr CDN (version-locked to package.json)
 * - Utils: Loaded via CDN on-demand (for phone validation/formatting)
 * 
 * This hybrid approach is industry standard:
 * - Build process only requires npm install
 * - No binary files committed to git
 * - CDN provides global caching for static assets
 * - Version consistency guaranteed via package.json
 */

import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.min.css';

// Configure flag image paths to use jsDelivr CDN (matches our npm package version)
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    const version = '26.5.1'; // Keep in sync with package.json
    style.textContent = `
        :root {
            --iti-path-flags-1x: url('https://cdn.jsdelivr.net/npm/intl-tel-input@${version}/build/img/flags.webp');
            --iti-path-flags-2x: url('https://cdn.jsdelivr.net/npm/intl-tel-input@${version}/build/img/flags@2x.webp');
        }
    `;
    document.head.appendChild(style);
}

class PhoneInputHandler {
    constructor() {
        this.iti = null;
        this.phoneInput = null;
        this.fullPhoneField = null;
        this.initialized = false;
    }

    /**
     * Initialize the phone input handler for a specific modal
     * @param {string} phoneInputSelector - CSS selector for the visible phone input
     * @param {string} fullPhoneFieldSelector - CSS selector for the hidden full phone field
     * @param {object} options - Configuration options for intl-tel-input
     */
    initialize(phoneInputSelector = '#user_basic_phone', fullPhoneFieldSelector = '#user_basic_phone_full', options = {}) {
        this.phoneInput = document.querySelector(phoneInputSelector);
        this.fullPhoneField = document.querySelector(fullPhoneFieldSelector);

        if (!this.phoneInput) {
            console.warn(`⚠️  Phone input not found: ${phoneInputSelector}`);
            return false;
        }

        // Destroy previous instance if it exists
        if (this.iti) {
            try {
                this.iti.destroy();
                this.iti = null;
            } catch (e) {
                console.warn('⚠️  Error destroying previous intl-tel-input instance:', e);
            }
        }

        try {
            // Check if intl-tel-input library is loaded from CDN
            if (typeof window.intlTelInput === 'undefined') {
                console.error('❌ intl-tel-input not loaded from CDN. Add script tag to Blade template.');
                return false;
            }

            // Check for existing phone number first
            const existingPhone = this.phoneInput.dataset.fullPhone || this.phoneInput.value;

            // Default options
            const defaultOptions = {
                separateDialCode: true,
                allowDropdown: true,
                initialCountry: 'us', // Always default to US, will be updated by setNumber() if needed
                // Note: v26.5.1 doesn't use utilsScript - utils are loaded automatically from CDN
                ...options
            };

            // Only use auto-detection geoIpLookup if there's NO existing phone number
            // If phone exists, setNumber() will detect the country from the phone itself
            if (!existingPhone || existingPhone.trim() === '') {
                defaultOptions.geoIpLookup = callback => {
                    fetch('/geo/country', { method: 'get' })
                        .then(res => res.json())
                        .then(data => {
                            callback(data.country || 'US');
                        })
                        .catch(() => {
                            callback('US');
                        });
                };
            }

            // Initialize intl-tel-input from global window object (loaded via CDN)
            this.iti = window.intlTelInput(this.phoneInput, defaultOptions);

            // Load existing phone number if available (this will set the correct country flag)
            if (existingPhone && existingPhone.trim() !== '') {
                this.iti.setNumber(existingPhone);
            }

            // Attach event listeners for real-time synchronization
            // Add small delay to ensure library is fully initialized
            setTimeout(() => {
                this.attachListeners();
                // Force sync after initialization
                this.syncPhoneFields();
            }, 100);

            this.initialized = true;
            return true;
        } catch (error) {
            console.error('❌ Error initializing intl-tel-input:', error.message);
            return false;
        }
    }

    /**
     * Attach event listeners to sync phone fields
     */
    attachListeners() {
        if (!this.phoneInput) return;

        // Listen for changes to keep the full phone field in sync
        const syncHandler = () => {
            // console.log('🔄 Phone input changed - syncing fields');
            this.syncPhoneFields();
        };

        this.phoneInput.addEventListener('change', syncHandler);
        this.phoneInput.addEventListener('blur', syncHandler);
        this.phoneInput.addEventListener('input', syncHandler);

        // Also sync on country change
        this.phoneInput.addEventListener('countrychange', syncHandler);
    }

    /**
     * Synchronize the full phone number from intl-tel-input to the hidden field
     */
    syncPhoneFields() {
        if (!this.iti || !this.fullPhoneField) {
            console.warn('⚠️ syncPhoneFields: iti or fullPhoneField not available');
            return;
        }

        try {
            let fullNumber = this.iti.getNumber();
            
            // If getNumber() returns empty, try to build it manually from country code + input value
            if (!fullNumber || fullNumber.trim() === '') {
                const countryData = this.iti.getSelectedCountryData();
                const rawNumber = this.phoneInput.value;
                
                if (countryData && rawNumber) {
                    // Prepend country code if not already present
                    fullNumber = rawNumber.startsWith('+') ? rawNumber : '+' + countryData.dialCode + rawNumber;
                }
            }
            
            if (fullNumber && fullNumber.trim() !== '') {
                this.fullPhoneField.value = fullNumber;
            } else {
                console.warn('⚠️ No valid phone to sync');
            }
        } catch (error) {
            console.warn('⚠️  Error syncing phone fields:', error.message);
        }
    }

    /**
     * Get the full phone number formatted by intl-tel-input
     * @returns {string} Formatted phone number or empty string
     */
    getPhoneNumber() {
        if (!this.iti) {
            return this.phoneInput ? this.phoneInput.value : '';
        }

        try {
            // First check if the number is valid
            if (this.iti.isValidNumber()) {
                const number = this.iti.getNumber();
                return number || '';
            } else {
                // If not valid, try to get the raw value with country code
                const countryData = this.iti.getSelectedCountryData();
                const rawNumber = this.phoneInput.value;
                
                // If raw number doesn't have +, prepend country code
                if (countryData && !rawNumber.startsWith('+')) {
                    return '+' + countryData.dialCode + rawNumber;
                }
                return rawNumber || '';
            }
        } catch (error) {
            console.warn('⚠️  Error getting phone number:', error.message);
            return this.phoneInput ? this.phoneInput.value : '';
        }
    }

    /**
     * Set the phone number in the input
     * @param {string} number - Phone number to set
     */
    setPhoneNumber(number) {
        if (!this.iti) return;

        try {
            this.iti.setNumber(number);
            this.syncPhoneFields();
        } catch (error) {
            console.warn('⚠️  Error setting phone number:', error.message);
        }
    }

    /**
     * Validate the phone number
     * @returns {boolean} Whether the phone number is valid
     */
    isPhoneNumberValid() {
        if (!this.iti) {
            return (this.phoneInput?.value || '').trim().length > 0;
        }

        try {
            return this.iti.isValidNumber();
        } catch (error) {
            console.warn('⚠️  Error validating phone number:', error.message);
            return false;
        }
    }

    /**
     * Reset the phone input
     */
    reset() {
        if (this.phoneInput) {
            this.phoneInput.value = '';
        }
        if (this.fullPhoneField) {
            this.fullPhoneField.value = '';
        }
        this.initialized = false;
    }

    /**
     * Destroy the intl-tel-input instance
     */
    destroy() {
        if (this.iti) {
            try {
                this.iti.destroy();
                this.iti = null;
            } catch (e) {
                console.warn('⚠️  Error destroying intl-tel-input:', e);
            }
        }
        this.initialized = false;
    }
}

// Export singleton instance
export default new PhoneInputHandler();
